/**
 * ConciergeCelebration — one-shot "ta-da" MAGICAL AURA overlay, fired once
 * when a recommended-agent card first lands in the Concierge chat stream
 * (expanding glow rings + rising motes; the card itself gets a shimmer via
 * data-celebrate).
 *
 * Mechanics:
 *   - `position: absolute; inset: 0; overflow: hidden; pointer-events: none`
 *     overlay on the chat panel — no page-level spill, no scrollbars;
 *   - auto-unmounts via `onDone` after ~1.6s max;
 *   - under prefers-reduced-motion it renders nothing and finishes
 *     immediately (the card's own gentle glow is the whole celebration).
 */

import { useEffect, useMemo } from 'react';
import { prefersReducedMotion } from './motion';
import './ConciergeCelebration.css';

export interface ConciergeCelebrationProps {
  /** Called when the effect finishes — the host should unmount the overlay. */
  onDone: () => void;
}

const CELEBRATION_MS = 1600;

export function ConciergeCelebration({ onDone }: ConciergeCelebrationProps) {
  const reduced = useMemo(prefersReducedMotion, []);

  useEffect(() => {
    if (reduced) {
      onDone();
      return;
    }
    const timer = window.setTimeout(onDone, CELEBRATION_MS);
    return () => window.clearTimeout(timer);
  }, [reduced, onDone]);

  if (reduced) return null;

  return (
    <div
      className="concierge-celebration"
      data-testid="concierge-celebration"
      aria-hidden="true"
    >
      <span className="cc-ring" />
      <span className="cc-ring cc-ring--2" />
      <span className="cc-ring cc-ring--3" />
      <span className="cc-mote cc-mote--1" />
      <span className="cc-mote cc-mote--2" />
      <span className="cc-mote cc-mote--3" />
      <span className="cc-mote cc-mote--4" />
      <span className="cc-mote cc-mote--5" />
      <span className="cc-mote cc-mote--6" />
    </div>
  );
}
